import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import ImageWithBasePath from "../../../../components/Common/imageWithBasePath";
import { all_routes } from "../../../../router/all_routes";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Form, Input, Upload, message } from "antd";
import useProfile from "../hooks/useProfile";
import Loader from "components/Common/loader";
import CustomModal from "components/Common/CustomModal";
import { UploadOutlined } from "@ant-design/icons";
import ImageWithoutBasePath from "components/Common/imageWithoutBasePath";
import UserRoleBadge from "components/Common/Badge/UserRoleBadge";
import PageHeader from "components/Common/PageHeader";
import CustomButton from "components/Common/ui/CustomButton";
import { CITIES_OPTIONS } from "constants/userConstants";
import CommonSelectLite from "components/Common/CommonSelectLite";
// import { CITIES_OPTIONS } from "../../../../../constants/cities";

type PasswordField =
  | "oldPassword"
  | "newPassword"
  | "confirmPassword"
  | "currentPassword";

type UserProfile = {
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  phoneNumber: string;
  address: string;
  country: string;
  state: string;
  city: string;
  postalCode: string;
  profileImage: string;
};

// Keep your current imports as-is

const Profile = () => {
  const maxSizeMB = process.env.REACT_APP_MAX_FILE_SIZE_MB;

  const { t } = useTranslation();
  const route = all_routes;
  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadingUpdateImage, setLoadingUpdateImage] = useState(false);

  const {
    profile,
    isLoading,
    getInfoUser,
    updateImageProfile,
    updateUserProfile,
  } = useProfile();

  useEffect(() => {
    getInfoUser();
  }, []);

  useEffect(() => {
    if (profile) {
      form.setFieldsValue({
        firstName: profile.firstName,
        lastName: profile.lastName,
        phoneNumber: profile.phoneNumber,
        address: profile.address,
        city: profile.city,
        zipCode: profile.zipCode,
        cityOfBirth: profile.cityOfBirth,
      });
    }
  }, [profile, form]);

  const handleUpdateImageProfile = async (profileImage: File | null) => {
    try {
      setLoadingUpdateImage(true);
      await updateImageProfile(profileImage);
    } catch (err) {
      console.error("Failed to update profile image:", err);
    }
    finally {
      setLoadingUpdateImage(false);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      const values = await form.validateFields();
      await updateUserProfile(values);
      setIsModalOpen(false);
      form.resetFields();
      message.success(t("profile_updated_successfully"));
    } catch (err) {
      console.error("Failed to update profile:", err);
    }
  };


  const openModalUpdateProfile = () => {
    form.setFieldsValue({
      firstName: profile?.firstName,
      lastName: profile?.lastName,
      phoneNumber: profile?.phoneNumber,
      address: profile?.address,
      city: profile?.city,
      zipCode: profile?.zipCode,
      cityOfBirth: profile?.cityOfBirth,
    });
    setIsModalOpen(true);
  };

  if (isLoading) return <Loader />;

  const breadcrumbItems = [
    { label: t("dashboard"), path: "/dashboard" },
    { label: t("profile"), active: true },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <PageHeader
          title={t("profile")}
          breadcrumbItems={breadcrumbItems}
        />

        <div className="d-md-flex d-block mt-3 gap-3">
          {/* Left side – Image + Upload */}
          <div className="col-md-4">
            <div className="card">
              <div className="card-header">
                <h5>{t("personal_information")}</h5>
              </div>
              <div className="card-body">
                <div className="settings-profile-upload">
                  <span style={{
                    width: 90,
                    height: 90,
                    borderRadius: "50%",
                    overflow: "hidden",
                    position: "relative",
                    backgroundColor: "#f5f5f5", // optional fallback
                    border: "2px solid #eee",   // optional border
                    display: "inline-block",
                    margin: "5px"
                  }}>
                    <ImageWithoutBasePath
                      loading={loadingUpdateImage}
                      src={
                        profile?.photo ||
                        "http://localhost:3000/assets/img/profiles/avatar-27.jpg"
                      }
                      alt="Profile"
                      width={150}
                      height={150}
                      style={{
                        objectFit: "cover",
                        width: "100%",
                        height: "100%",
                        display: "block",
                      }}
                    />
                  </span>
                  <div className="title-upload">
                    <Link to="#" className="me-2 text-danger" onClick={() => updateImageProfile(null)}>
                      {t("remove")}
                    </Link>
                    <Upload
                      disabled={loadingUpdateImage}
                      customRequest={({ file, onSuccess }) => {
                        handleUpdateImageProfile(file as File);
                        if (onSuccess) setTimeout(() => onSuccess("ok"), 0);
                      }}
                      showUploadList={false}
                      accept=".jpg,.jpeg,.png"
                    >
                      <Link to="#" className="text-primary">{t("update")}</Link>
                    </Upload>
                  </div>
                </div>

                <div className="profile-uploader profile-uploader-two mb-0">
                  <Upload.Dragger
                    disabled={loadingUpdateImage}
                    customRequest={({ file, onSuccess }) => {
                      handleUpdateImageProfile(file as File);
                      if (onSuccess) setTimeout(() => onSuccess("ok"), 0);
                    }}
                    showUploadList={false}
                    accept=".jpg,.jpeg,.png"
                  >
                    <span className="upload-icon">
                      <i className="ti ti-upload" />
                    </span>
                    <div className="drag-upload-btn bg-transparent me-0 border-0">
                      <h6 className="upload-btn">
                        -{t("max_file_size_to_upload_image", { maxSize: maxSizeMB })}
                        <br />
                        -{t("click_to_upload_image", { format: "jpg,jpeg,png" })}
                        <br />
                        -{t("upload_image_recommendation", { width: 320, height: 320 })}
                      </h6>
                    </div>
                  </Upload.Dragger>
                </div>




              </div>
            </div>
          </div>

          {/* Right side – Info Card */}
          <div className="col-md-8">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5>{t("personal_information")}</h5>
                <CustomButton
                  variant="primary"
                  onClick={() => openModalUpdateProfile()}
                >
                  <i className="ti ti-edit me-2" />
                  {t("edit")}
                </CustomButton>
              </div>
              <div className="card-body pb-0">
                <div className="mb-3">
                  {/* <UserRoleBadge role={profile?.role || "guest"} /> */}
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">{t("first_name")}</label>
                    <div className="form-control">{profile?.firstName || "-"}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">{t("last_name")}</label>
                    <div className="form-control">{profile?.lastName || "-"}</div>
                  </div>
                  <div className="col-md-12 mb-3">
                    <label className="form-label">{t("email_address")}</label>
                    <div className="form-control">{profile?.email || "-"}</div>
                  </div>
                  <div className="col-md-12 mb-3">
                    <label className="form-label">{t("phone_number")}</label>
                    <div className="form-control">{profile?.phoneNumber || "-"}</div>
                  </div>
                  <div className="col-md-12 mb-3">
                    <label className="form-label">{t("address")}</label>
                    <div className="form-control">{profile?.address || "-"}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">{t("city")}</label>
                    <div className="form-control">{profile?.city || "-"}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">{t("zip_code")}</label>
                    <div className="form-control">{profile?.zipCode || "-"}</div>
                  </div>
                  <div className="col-md-12 mb-3">
                    <label className="form-label">{t("city_of_birth")}</label>
                    <div className="form-control">{profile?.cityOfBirth || "-"}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <CustomModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          form.resetFields();
        }}
        onSave={handleUpdateProfile}
        loading={isLoading}
        saveText={t("save")}
        title={t("edit_personal_information")}
      >
        <Form form={form} layout="vertical">
          <div className="row">
            <div className="col-md-6">
              <Form.Item
                label={t("first_name")}
                name="firstName"
                rules={[{ required: true, message: t("validation.please_enter_first_name") }]}
              >
                <Input placeholder={t("first_name")} />
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item
                label={t("last_name")}
                name="lastName"
                rules={[{ required: true, message: t("validation.please_enter_last_name") }]}
              >
                <Input placeholder={t("last_name")} />
              </Form.Item>
            </div>
            <div className="col-md-12">
              <Form.Item
                label={t("phone_number")}
                name="phoneNumber"
                rules={[{ required: true, message: t("validation.please_enter_phone") }]}
              >
                <Input placeholder={t("phone_number")} />
              </Form.Item>
            </div>
            <div className="col-md-12">
              <Form.Item label={t("address")} name="address">
                <Input placeholder={t("address")} />
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item label={t("city")} name="city">
                {/* <Input placeholder={t("city")} /> */}
                <CommonSelectLite
                  options={CITIES_OPTIONS}
                  optionValueKey="name"
                  optionLabelKey="name"
                  placeholder={t("enter_city")}
                  value={form.getFieldValue("city")}
                  onChange={(value) =>
                    form.setFieldsValue({
                      city: value,
                    })
                  }
                />
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item label={t("zip_code")} name="zipCode">
                <Input placeholder={t("zip_code")} />
              </Form.Item>
            </div>
            <div className="col-md-12">
              <Form.Item label={t("city_of_birth")} name="cityOfBirth">
              <CommonSelectLite
                  options={CITIES_OPTIONS}
                  optionValueKey="name"
                  optionLabelKey="name"
                  placeholder={t("city_of_birth")}
                  value={form.getFieldValue("cityOfBirth")}
                  onChange={(value) =>
                    form.setFieldsValue({
                      cityOfBirth: value,
                    })
                  }
                />
              </Form.Item>
            </div>
          </div>
        </Form>
      </CustomModal>
    </div>
  );
};

export default Profile;


